import "./PageTitlebar.scss";

import { FC } from "react";
import { twMerge } from "tailwind-merge";

import { AI_SUMMARIZE_RESPONSES_MESSAGE, sendAIChatQuery } from "components/ai/queries/chat-query";
import { SECONDARY_OVERLAY_QUERY_PARAM, SecondaryOverlay } from "components/constants";
import { HoverableEditField } from "components/hover-widgets/EditableField";
import { HoverableToolbar } from "components/hover-widgets/HoverableToolbar";
import { PageNavigationButtons } from "components/materials/ContentNavigationButtons";
import { useIsAssessing } from "components/materials/section/assessment/hooks";
import { PageSidebarOptionsMenu } from "components/menus/PageSidebarOptionsMenu";
import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { useCapabilities } from "store/capability-hooks";
import { useAppDispatch, useAppSelector } from "store/index";
import { selectCurrentUserIsMPAdmin, selectIsEditModeEnabled } from "store/selectors";
import { aiStateActions } from "store/slices/ai";
import { usePageActions } from "utils/page";
import { useQueryParamsManager } from "utils/routing";

type PageTitlebarProps = {
  page: PageType;
  selectedTopicId?: string;
  pages: PageType[];
};

export const PageTitlebar: FC<PageTitlebarProps> = ({ page, selectedTopicId, pages }) => {
  const isMobile = !useIsMdOrLarger();
  const { canAuthorCourse } = useCapabilities();
  const isAssessing = useIsAssessing();
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);
  const isMPAdmin = useAppSelector(selectCurrentUserIsMPAdmin);
  const dispatch = useAppDispatch();
  const { setQueryParams } = useQueryParamsManager();
  const canSummarizeResponses = canAuthorCourse && !isAssessing;

  const { savePage, navigateToPage } = usePageActions();

  const onPageTitleChanged = (newTitle: string) => savePage({ id: page.id, title: newTitle });
  const onPageChange = (selectedPageId: string, pageUrl: URL) => {
    const selectedPage = pages.find((p) => p.id === selectedPageId);
    // TODO: Don't even pass the URL object, just the pathname + search, in the first place
    navigateToPage(selectedPage, pageUrl.pathname + pageUrl.search);
  };

  const onSummarizeResponses = () => {
    // Ensure AI tab is open
    setQueryParams({ [SECONDARY_OVERLAY_QUERY_PARAM]: SecondaryOverlay.AIAssistant });

    // Ensure student response are enabled first
    dispatch(aiStateActions.setStudentResponsesForAIEnabled(true));
    dispatch(aiStateActions.clearAiChatMessages());

    // Note that this query acts on the _latest_ version of all the student responses, not their submissions,
    // which we might want to call out or allow changing.
    sendAIChatQuery(dispatch, page.section_id, AI_SUMMARIZE_RESPONSES_MESSAGE);
  };

  return (
    <div className="flex w-full shrink-0 grow-0 flex-col items-center px-4 md:px-10">
      <div className={twMerge("page-titlebar mb-4", isMobile ? "mt-6" : "mt-8")}>
        <div className="title-text -my-1 flex-auto pr-2">
          <HoverableToolbar
            className="relative"
            contentClassName="mt-1 bg-white"
            readOnly={!canAuthorCourse}
            reference="page"
            disableUnlessEditMode
            showEditButton
            uncentered
          >
            <h2>
              <HoverableEditField
                label={t("glossary.page_title")}
                textSize="h2"
                value={page.title}
                disableUnlessEditMode
                shouldWrap
                onValueChanged={onPageTitleChanged}
              />
            </h2>
          </HoverableToolbar>
        </div>

        <div
          className={twMerge(
            "navigation-buttons flex items-center gap-2",
            isAssessing ? "" : "mr-4", // fixes an issue where PageTitlebar's right padding disappears on smaller screens
          )}
        >
          {canAuthorCourse && (
            <PageSidebarOptionsMenu
              currentTopicId={selectedTopicId}
              direction="bottom"
              editMode={isEditModeEnabled}
              kind="gray"
              page={page}
              showDownloadUploadOptions={isMPAdmin}
              showTimeline={canAuthorCourse}
              onSummarizeResponses={canSummarizeResponses ? onSummarizeResponses : undefined}
            />
          )}

          {!isAssessing && <PageNavigationButtons pages={pages} onChange={onPageChange} />}
        </div>
      </div>
    </div>
  );
};
